import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Zap, Activity, Shield, Heart, Fingerprint, Wifi, Users, Send, CheckCircle2, AlertTriangle, Radio, Loader2, Volume2 } from 'lucide-react';
import { Contact, SafetyStatus, IncomingRequest } from '../types';
import { contactService, getEffectiveAvatar } from '../services/contactService';
import { soundService } from '../services/soundService';

interface PulsePageProps {
    currentUser: Contact;
    contacts: Contact[];
    onBack: () => void;
    onSendPulse: (contactId: string) => void;
    incomingRequest?: IncomingRequest | null;
}

// Pulse Response Types
type PulseStatus = 'SAFE' | 'OK' | 'NEED_HELP' | 'DANGER' | 'BUSY' | null;

const PulsePage: React.FC<PulsePageProps> = ({
    currentUser,
    contacts,
    onBack,
    onSendPulse,
    incomingRequest
}) => {
    // --- STATE ---
    const [mode, setMode] = useState<'broadcast' | 'individual' | 'respond'>('broadcast');
    const [isHolding, setIsHolding] = useState(false);
    const [scanProgress, setScanProgress] = useState(0);
    const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
    const [isSending, setIsSending] = useState(false);
    const [sentStatus, setSentStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
    const [acks, setAcks] = useState<{ id: string, name: string, status: string, avatar?: string }[]>([]);
    const [myResponse, setMyResponse] = useState<PulseStatus>(null);

    // Scan Interval
    const scanInterval = useRef<NodeJS.Timeout | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    // Quick Response Options
    const responseOptions = [
        { status: 'SAFE', icon: '✅', label: 'I\'m Safe', color: '#10b981' },
        { status: 'OK', icon: '👍', label: 'OK / Busy', color: '#3b82f6' },
        { status: 'NEED_HELP', icon: '🆘', label: 'Need Help', color: '#f59e0b' },
        { status: 'DANGER', icon: '⚠️', label: 'In Danger', color: '#ef4444' },
    ];

    // Check for incoming request on mount
    useEffect(() => {
        if (incomingRequest) {
            setMode('respond');
            // Play pulse sound
            try {
                soundService.playPulseSound();
            } catch (e) {
                console.warn('Audio blocked');
            }
        }
    }, [incomingRequest]);

    // Subscribe to ACKs
    useEffect(() => {
        const sub = contactService.subscribeToMessages(currentUser.id, (payload) => {
            const msg = payload.new;
            if (msg.receiver_id === currentUser.id && msg.content.startsWith('::PULSE_ACK::')) {
                const status = msg.content.replace('::PULSE_ACK::', '') || 'SAFE';
                const sender = contacts.find(c => c.id === msg.sender_id);
                const senderName = sender?.name || 'Unknown Agent';

                // Add to ACKs
                setAcks(prev => {
                    // Avoid duplicates
                    if (prev.find(a => a.id === msg.sender_id)) return prev;
                    return [...prev, { id: msg.sender_id, name: senderName, status, avatar: sender?.avatarUrl }];
                });

                // Haptic
                if (navigator.vibrate) navigator.vibrate([50, 50, 100]);
                soundService.playCheckSound();
            }
        });
        return () => { sub.unsubscribe(); };
    }, [currentUser.id, contacts]);

    // Handle Hold for Broadcast
    const startBroadcastHold = () => {
        setIsHolding(true);
        setScanProgress(0);
        if (navigator.vibrate) navigator.vibrate(20);

        scanInterval.current = setInterval(() => {
            setScanProgress(prev => {
                if (prev >= 100) {
                    completeBroadcast();
                    return 100;
                }
                return prev + 2;
            });
        }, 16);
    };

    const stopHold = () => {
        setIsHolding(false);
        setScanProgress(0);
        if (scanInterval.current) clearInterval(scanInterval.current);
    };

    const completeBroadcast = () => {
        if (scanInterval.current) clearInterval(scanInterval.current);
        if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
        soundService.playPulseSound();

        if (incomingRequest) {
            handleRespond('SAFE');
            setSentStatus('sent');
            setIsHolding(false);
            setScanProgress(0);
            return;
        }

        // Broadcast to all contacts (Sender Mode)
        setIsSending(true);
        setSentStatus('sending');

        contacts.forEach(c => onSendPulse(c.id));

        setTimeout(() => {
            setIsSending(false);
            setSentStatus('sent');
            setIsHolding(false);
            setScanProgress(0);
        }, 800);
    };

    // Send to selected contacts only
    const sendToSelected = () => {
        if (selectedContacts.length === 0) return;

        setIsSending(true);
        setSentStatus('sending');
        soundService.playPulseSound();

        selectedContacts.forEach(id => onSendPulse(id));

        setTimeout(() => {
            setIsSending(false);
            setSentStatus('sent');
            setSelectedContacts([]);
        }, 800);
    };

    // Respond to incoming pulse
    const handleRespond = async (status: PulseStatus) => {
        if (!status || !incomingRequest) return;

        setMyResponse(status);
        if (navigator.vibrate) navigator.vibrate(100);
        soundService.playCheckSound();

        // Send ACK back
        await contactService.sendMessage(
            currentUser.id,
            incomingRequest.senderId,
            `::PULSE_ACK::${status}`
        );

        // Delay then go back
        setTimeout(() => {
            onBack();
        }, 1500);
    };

    const toggleContactSelection = (id: string) => {
        setSelectedContacts(prev =>
            prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
        );
    };

    return (
        <div className="fixed inset-0 z-[100] bg-black text-white font-sans overflow-hidden select-none">

            {/* AMBIENT BACKGROUND */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,#0a1628_0%,#000000_100%)]" />
            <div className="absolute inset-0 opacity-10 bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />

            {/* Grid Overlay */}
            <div className="absolute inset-0 opacity-5 bg-[linear-gradient(rgba(0,240,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,240,255,0.1)_1px,transparent_1px)] bg-[size:50px_50px]" />

            {/* BACK BUTTON */}
            <button
                onClick={onBack}
                className="absolute top-6 left-6 z-20 p-3 rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10 transition backdrop-blur-md"
            >
                <ChevronLeft />
            </button>

            {/* HEADER */}
            <div className="absolute top-8 w-full text-center z-10 pointer-events-none">
                <h1 className="text-3xl font-black tracking-tighter text-white flex items-center justify-center gap-3">
                    <div className="relative">
                        <Radio className="text-violet-400" size={28} />
                        <div className="absolute inset-0 animate-ping opacity-50">
                            <Radio className="text-violet-400" size={28} />
                        </div>
                    </div>
                    NEURAL PULSE
                </h1>
                <p className="text-[10px] text-violet-400/60 font-mono tracking-[0.3em] mt-1 uppercase">
                    Squad Synchronization System
                </p>
            </div>

            {/* MODE TABS */}
            {!incomingRequest && (
                <div className="absolute top-24 w-full flex justify-center z-10 px-6">
                    <div className="flex bg-white/5 backdrop-blur-md rounded-full p-1 border border-white/10">
                        <button
                            onClick={() => setMode('broadcast')}
                            className={`px-6 py-2 rounded-full text-xs font-bold tracking-wider transition-all ${mode === 'broadcast'
                                ? 'bg-violet-500 text-white shadow-[0_0_20px_rgba(139,92,246,0.4)]'
                                : 'text-gray-400 hover:text-white'
                                }`}
                        >
                            BROADCAST
                        </button>
                        <button
                            onClick={() => setMode('individual')}
                            className={`px-6 py-2 rounded-full text-xs font-bold tracking-wider transition-all ${mode === 'individual'
                                ? 'bg-cyan-500 text-white shadow-[0_0_20px_rgba(6,182,212,0.4)]'
                                : 'text-gray-400 hover:text-white'
                                }`}
                        >
                            SELECT
                        </button>
                    </div>
                </div>
            )}

            {/* MAIN CONTENT */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pt-20">

                {/* BROADCAST MODE */}
                {mode === 'broadcast' && !incomingRequest && (
                    <div className="flex flex-col items-center">
                        {/* Central Scanner */}
                        <div
                            className="relative w-64 h-64 flex items-center justify-center cursor-pointer"
                            onPointerDown={startBroadcastHold}
                            onPointerUp={stopHold}
                            onPointerLeave={stopHold}
                        >
                            {/* Ripple Rings */}
                            <AnimatePresence>
                                {isHolding && (
                                    <>
                                        <motion.div
                                            initial={{ scale: 1, opacity: 0.5 }}
                                            animate={{ scale: 2.5, opacity: 0 }}
                                            transition={{ repeat: Infinity, duration: 1.5 }}
                                            className="absolute inset-0 rounded-full border-2 border-violet-500/50"
                                        />
                                        <motion.div
                                            initial={{ scale: 1, opacity: 0.3 }}
                                            animate={{ scale: 2, opacity: 0 }}
                                            transition={{ repeat: Infinity, duration: 1.5, delay: 0.4 }}
                                            className="absolute inset-0 rounded-full border border-cyan-500/30"
                                        />
                                    </>
                                )}
                            </AnimatePresence>

                            {/* Sent Animation */}
                            <AnimatePresence>
                                {sentStatus === 'sent' && (
                                    <motion.div
                                        initial={{ scale: 0, opacity: 1 }}
                                        animate={{ scale: 3, opacity: 0 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 1 }}
                                        className="absolute inset-0 rounded-full bg-emerald-500/30"
                                    />
                                )}
                            </AnimatePresence>

                            {/* Central Button */}
                            <motion.div
                                animate={{
                                    scale: isHolding ? 0.9 : 1,
                                    boxShadow: isHolding
                                        ? '0 0 80px rgba(139,92,246,0.6)'
                                        : '0 0 40px rgba(139,92,246,0.3)'
                                }}
                                className={`
                                    w-44 h-44 rounded-full bg-[#0a0f1a] border-2 flex items-center justify-center relative
                                    ${isHolding ? 'border-violet-400' : 'border-white/20'}
                                    transition-colors duration-300
                                `}
                            >
                                {/* Dashed Orbit */}
                                <div className="absolute inset-4 rounded-full border border-dashed border-white/10 animate-[spin_20s_linear_infinite]" />

                                {/* Content */}
                                {sentStatus === 'sent' ? (
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        className="flex flex-col items-center text-emerald-400"
                                    >
                                        <CheckCircle2 size={48} />
                                        <span className="text-xs font-bold mt-2 tracking-widest">SENT</span>
                                    </motion.div>
                                ) : isHolding ? (
                                    <div className="flex flex-col items-center">
                                        <span className="text-3xl font-black text-violet-400">{Math.floor(scanProgress)}%</span>
                                        <span className="text-[9px] tracking-widest text-violet-500/70 mt-1">BROADCASTING</span>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center">
                                        <Wifi size={48} className="text-white/30" />
                                        <span className="text-[10px] tracking-widest text-white/40 mt-2">HOLD TO SEND</span>
                                    </div>
                                )}

                                {/* Progress Ring */}
                                <svg className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none">
                                    <circle cx="50%" cy="50%" r="48%" stroke="transparent" strokeWidth="4" fill="none" />
                                    <circle
                                        cx="50%" cy="50%" r="48%"
                                        stroke="#8b5cf6" strokeWidth="4"
                                        strokeDasharray="300%"
                                        strokeDashoffset={`${300 - (scanProgress * 3)}%`}
                                        strokeLinecap="round"
                                        fill="none"
                                        className="transition-all duration-75"
                                        style={{ filter: 'drop-shadow(0 0 6px #8b5cf6)' }}
                                    />
                                </svg>
                            </motion.div>
                        </div>

                        <p className="mt-6 text-xs font-mono text-gray-500 uppercase tracking-widest">
                            {sentStatus === 'sent' ? 'Pulse broadcasted to squad' : 'Hold to check on your squad'}
                        </p>
                    </div>
                )}

                {/* INDIVIDUAL SELECT MODE */}
                {mode === 'individual' && !incomingRequest && (
                    <div className="flex flex-col items-center w-full max-w-md px-6">
                        <div className="w-full mb-6">
                            <p className="text-sm text-gray-400 mb-4 text-center">Select contacts to pulse:</p>
                            <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                                {contacts.map(c => {
                                    const isSelected = selectedContacts.includes(c.id);
                                    const ack = acks.find(a => a.id === c.id);
                                    return (
                                        <motion.button
                                            key={c.id}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => toggleContactSelection(c.id)}
                                            className={`
                                                w-full flex items-center gap-4 p-4 rounded-xl backdrop-blur-md border transition-all
                                                ${isSelected
                                                    ? 'bg-cyan-500/20 border-cyan-500/50'
                                                    : 'bg-white/5 border-white/10 hover:bg-white/10'}
                                                ${ack ? 'border-emerald-500/50 bg-emerald-500/10' : ''}
                                            `}
                                        >
                                            <div className="relative">
                                                <img
                                                    src={getEffectiveAvatar(c)}
                                                    className="w-12 h-12 rounded-full object-cover border border-white/10"
                                                />
                                                {isSelected && !ack && (
                                                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-cyan-500 rounded-full flex items-center justify-center">
                                                        <CheckCircle2 size={12} />
                                                    </div>
                                                )}
                                                {ack && (
                                                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center text-[10px]">
                                                        ✅
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex-1 text-left">
                                                <div className="font-bold text-white">{c.name}</div>
                                                <div className="text-xs text-gray-400">{c.role}</div>
                                            </div>
                                            {ack && (
                                                <span className="text-xs font-bold text-emerald-400 uppercase">{ack.status}</span>
                                            )}
                                        </motion.button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Send Button */}
                        <button
                            onClick={sendToSelected}
                            disabled={selectedContacts.length === 0 || isSending}
                            className={`
                                w-full py-4 rounded-xl font-bold tracking-wider transition-all flex items-center justify-center gap-3
                                ${selectedContacts.length > 0 && !isSending
                                    ? 'bg-cyan-500 text-white shadow-[0_0_30px_rgba(6,182,212,0.4)] hover:bg-cyan-400'
                                    : 'bg-white/10 text-gray-500 cursor-not-allowed'}
                            `}
                        >
                            {isSending ? (
                                <Loader2 className="animate-spin" size={20} />
                            ) : (
                                <>
                                    <Send size={18} />
                                    SEND PULSE ({selectedContacts.length})
                                </>
                            )}
                        </button>
                    </div>
                )}

                {/* RESPOND MODE (Incoming Pulse) */}
                {incomingRequest && (
                    <div className="flex flex-col items-center w-full h-full justify-center">

                        {/* Target Lock Animation */}
                        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                            <div className="w-[300px] h-[300px] rounded-full border border-red-500/30 animate-pulse absolute" />
                            <div className="w-[280px] h-[280px] rounded-full border border-dashed border-red-500/50 animate-[spin_10s_linear_infinite] absolute" />
                        </div>

                        {/* Incoming Alert Header */}
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="mb-10 text-center z-20"
                        >
                            <h2 className="text-3xl font-black text-white tracking-tighter">INCOMING PULSE</h2>
                            <div className="flex items-center justify-center gap-2 mt-2">
                                <span className="text-violet-400 font-bold bg-violet-500/10 px-3 py-1 rounded-full border border-violet-500/30">
                                    {incomingRequest.senderName}
                                </span>
                                <span className="text-gray-500 text-xs font-mono">REQING STATUS...</span>
                            </div>
                        </motion.div>

                        {/* RESPONSE TRIGGER (HOLD TO SYNC) */}
                        <div
                            className="relative w-64 h-64 flex items-center justify-center cursor-pointer z-20"
                            onPointerDown={() => startBroadcastHold()} // Reusing hold logic for response
                            onPointerUp={stopHold}
                            onPointerLeave={stopHold}
                        >
                            {/* Ripple Rings */}
                            <AnimatePresence>
                                {isHolding && (
                                    <motion.div
                                        initial={{ scale: 1, opacity: 0.5 }}
                                        animate={{ scale: 2.5, opacity: 0 }}
                                        transition={{ repeat: Infinity, duration: 1.5 }}
                                        className="absolute inset-0 rounded-full border-2 border-emerald-500/50" // Green for SAFE response
                                    />
                                )}
                            </AnimatePresence>

                            {/* Button */}
                            <motion.div
                                animate={{
                                    scale: isHolding ? 0.9 : 1,
                                    boxShadow: isHolding
                                        ? '0 0 80px rgba(16,185,129,0.6)' // Green Glow
                                        : '0 0 40px rgba(139,92,246,0.2)'
                                }}
                                className={`
                                    w-44 h-44 rounded-full bg-[#0a0f1a] border-2 flex items-center justify-center relative
                                    ${isHolding ? 'border-emerald-400' : 'border-white/20'}
                                    transition-colors duration-300
                                `}
                            >
                                {/* Progress Ring */}
                                <svg className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none">
                                    <circle cx="50%" cy="50%" r="48%" stroke="transparent" strokeWidth="4" fill="none" />
                                    <circle
                                        cx="50%" cy="50%" r="48%"
                                        stroke="#10b981" // Green
                                        strokeWidth="4"
                                        strokeDasharray="300%"
                                        strokeDashoffset={`${300 - (scanProgress * 3)}%`}
                                        strokeLinecap="round"
                                        fill="none"
                                        className="transition-all duration-75"
                                        style={{ filter: 'drop-shadow(0 0 6px #10b981)' }}
                                    />
                                </svg>

                                {myResponse ? (
                                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-center">
                                        <CheckCircle2 size={48} className="text-emerald-400 mx-auto" />
                                        <span className="text-xs font-bold text-emerald-400 mt-2 block tracking-widest">SENT</span>
                                    </motion.div>
                                ) : isHolding ? (
                                    <div className="flex flex-col items-center">
                                        <span className="text-3xl font-black text-emerald-400">{Math.floor(scanProgress)}%</span>
                                        <span className="text-[9px] tracking-widest text-emerald-500/70 mt-1">SYNCING STATUS</span>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center">
                                        <Fingerprint size={48} className="text-white/30" />
                                        <span className="text-[10px] tracking-widest text-white/40 mt-2">HOLD TO CONFIRM SAFE</span>
                                    </div>
                                )}
                            </motion.div>
                        </div>

                        {/* Alternate Options (SOS) */}
                        {!isHolding && !myResponse && (
                            <div className="mt-8 flex gap-4">
                                <button
                                    onClick={() => handleRespond('DANGER')}
                                    className="px-6 py-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-500 font-bold text-xs tracking-wider hover:bg-red-500/20 transition"
                                >
                                    ⚠️ IM IN DANGER
                                </button>
                                <button
                                    onClick={() => handleRespond('OK')}
                                    className="px-6 py-3 rounded-xl bg-blue-500/10 border border-blue-500/30 text-blue-500 font-bold text-xs tracking-wider hover:bg-blue-500/20 transition"
                                >
                                    👍 IM BUSY
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* SQUAD STATUS (Bottom) */}
            {mode === 'broadcast' && acks.length > 0 && (
                <div className="absolute bottom-8 w-full px-6">
                    <p className="text-center text-xs text-gray-500 mb-3 font-mono uppercase tracking-widest">
                        {acks.length} / {contacts.length} RESPONDED
                    </p>
                    <div className="flex items-center justify-center gap-3 flex-wrap">
                        {acks.map(a => (
                            <motion.div
                                key={a.id}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="flex items-center gap-2 px-3 py-2 rounded-xl backdrop-blur-md border bg-emerald-500/10 border-emerald-500/40 shadow-[0_0_20px_rgba(16,185,129,0.2)]"
                            >
                                <img src={a.avatar || `https://i.pravatar.cc/50?u=${a.id}`} className="w-8 h-8 rounded-full object-cover" />
                                <div className="flex flex-col">
                                    <span className="text-[10px] text-emerald-400 font-bold uppercase">{a.status}</span>
                                    <span className="text-xs font-bold text-white">{a.name}</span>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PulsePage;
